import { supabase } from '../lib/supabase';

/**
 * Retell Sync Service
 * Talks to the `retell-agent-sync` edge function (super-admin only). Mirrors
 * the fetch/header shape of `resolveRetellConfig` in VoiceCallContext.jsx.
 *
 * Never throws: every failure path (missing session, non-OK response,
 * network rejection, thrown error) resolves to `{ data: null, error }` so
 * callers -- including the save flow, which must not fail on a sync error --
 * can always safely await this without a try/catch.
 */
const postToRetellSync = async (payload, { signedOutMessage, failureLabel, logLabel }) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { data: null, error: signedOutMessage };
    }

    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/retell-agent-sync`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      return { data: null, error: data.error || `${failureLabel} (${res.status})` };
    }

    return { data, error: null };
  } catch (err) {
    console.error(`Error in ${logLabel}:`, err);
    return { data: null, error: err.message };
  }
};

/** Pushes one vapi_assistants row to its per-coach Retell LLM + agent. */
export const syncAssistantToRetell = (assistantId) =>
  postToRetellSync({ assistant_id: assistantId }, {
    signedOutMessage: 'You must be signed in to sync.',
    failureLabel: 'Retell sync failed',
    logLabel: 'syncAssistantToRetell',
  });

/**
 * Adds a Cartesia voice (by its Cartesia voice id) to the Retell voice library
 * and resolves to `{ voice_id, voice_name }`, where voice_id is the Retell id
 * to store as retell_voice_id. The voice must be shared in Cartesia; Retell
 * cannot reach a private one.
 */
export const addCartesiaVoiceToRetell = (providerVoiceId, voiceName) =>
  postToRetellSync({ action: 'add_voice', provider_voice_id: providerVoiceId, voice_name: voiceName }, {
    signedOutMessage: 'You must be signed in to add a voice.',
    failureLabel: 'Adding the voice to Retell failed',
    logLabel: 'addCartesiaVoiceToRetell',
  });

export default { syncAssistantToRetell, addCartesiaVoiceToRetell };
